/* Billing wording and numbers, in one place. */

/* Transcribed from BillingHistory.jsx. What customers pay for is Membership;
   the web-profile subscription type is shown as "Web Profile". */
const TYPE_LABELS = { verified: "Membership", "pay-as-you-go": "Web Profile" };

export const planLabel = (subs) =>
  subs?.PaymentIntentID
    ? "Membership"
    : TYPE_LABELS[subs?.subscription_type] || subs?.subscription_type;

/* Transcribed from Subscription.jsx's `plan` table. Only a fallback: a row
   carrying its own `amount` uses that instead — the verified rows do, which is
   how 99 (monthly) and 999 (annual) both come from one subscription_type. */
export const PLAN_AMOUNTS = {
  "pay-as-you-go": 79,
  basic: 195,
  pro: 275,
  premium: 500,
  verified: 99,
};

export const getSubAmount = (subscriptionType) => PLAN_AMOUNTS[subscriptionType];

/* The amount actually shown on a row. Transcribed:
   `subs?.amount ? subs?.amount : getSubAmount(subs?.subscription_type)` — so a
   zero amount falls through to the plan table, as it does today. */
export const rowAmount = (subs) =>
  subs?.amount ? subs.amount : getSubAmount(subs?.subscription_type);

/* Mirrors common/Money.jsx's formatter: en-AE grouping, always two decimals,
   a leading minus for negatives. Kept as one function so no row can format an
   amount differently from the hero above it. */
export const formatAed = (value) =>
  Math.abs(Number(value) || 0).toLocaleString("en-AE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const isNegative = (value) => Number(value) < 0;

/* "11th July 2027" — transcribed from Subscription.jsx's formatRenewalDate, including its two fallbacks: nothing in gives an empty string, and an unparseable... */
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export const formatRenewalDate = (dateString) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return dateString;
  const day = d.getDate();
  const j = day % 10;
  const k = day % 100;
  const suffix =
    j === 1 && k !== 11 ? "st" :
    j === 2 && k !== 12 ? "nd" :
    j === 3 && k !== 13 ? "rd" : "th";
  return `${day}${suffix} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

/* The invoice's name in the first column: "Invoice_Jul 2026". Transcribed —
   the live cell builds it from date_of_start, not from created_at. */
export const invoiceName = (subs) => {
  const d = new Date(subs?.date_of_start);
  if (Number.isNaN(d.getTime())) return "Invoice";
  return `Invoice_${d.toLocaleDateString("en-US", { month: "short", year: "numeric" })}`;
};

/* The live cell also renders `subs?.invoice?.slice(29)` beside that label — the tail of the stored invoice path. */
export const invoiceFileHint = (subs) =>
  typeof subs?.invoice === "string" ? subs.invoice.slice(29) : "";

/* "Paid on". Transcribed including the TIP-556 note: created_at carries the
   purchase time, and rows older than that field show date-only because the
   time is genuinely unknown. */
export const paidOn = (subs) => {
  if (subs?.created_at) {
    const d = new Date(subs.created_at);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    }
  }
  const start = new Date(subs?.date_of_start);
  if (Number.isNaN(start.getTime())) return "";
  return start.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/* Which document a row's download points at. Transcribed from TIP-556: the
   TREVEX-generated tax invoice is the primary document, the Stripe-hosted
   receipt only a fallback for rows whose PDF was never generated. */
export const invoiceHref = (subs) => subs?.trevex_invoice || subs?.invoice_url;

export const invoiceTitle = (subs) =>
  subs?.trevex_invoice ? "Download TREVEX tax invoice" : "Download Stripe receipt";

/* Transcribed from BillingHistory.jsx: freemium rows never appear in the
   history table. */
export const payableRows = (data = []) =>
  (data || []).filter((row) => row?.subscription_type !== "freemium");

/* The footer total. New to this screen — the design asks for it — and derived
   from the same rows the table renders, so it cannot disagree with them. */
export const totalPaid = (rows = []) =>
  (rows || []).reduce((sum, row) => sum + (Number(rowAmount(row)) || 0), 0);
